// 내 단축 링크 목록 — 복사·수정·QR·통계·비활성화
import { useState } from "react";
import { linkApi, type Link } from "../api/links.ts";
import { ApiError } from "@taplink/web-shared";

interface Props {
  links: Link[];
  /** 조직 작업 공간에서는 동료가 만든 링크도 함께 보인다(IMP-5) — 내 것만 보는 필터를 준다 */
  myAccountId?: number | undefined;
  onChanged: () => void;
  onEdit: (link: Link) => void;
  onShowQr: (link: Link) => void;
  onShowStats: (link: Link) => void;
}

export function LinkList({ links, myAccountId, onChanged, onEdit, onShowQr, onShowStats }: Props) {
  const [copied, setCopied] = useState<string | null>(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const copy = async (link: Link) => {
    try {
      await navigator.clipboard.writeText(link.shortUrl);
      setCopied(link.shortKey);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setError("클립보드 복사에 실패했습니다.");
    }
  };

  const deactivate = async (link: Link) => {
    if (!window.confirm(`이 링크를 비활성화할까요?\n${link.shortUrl}\n\n이후 접속하면 차단됩니다.\n나중에 다시 활성화할 수 있습니다.`)) return;
    setBusy(link.shortKey);
    setError(null);
    try {
      await linkApi.remove(link.shortKey);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "처리에 실패했습니다.");
    } finally {
      setBusy(null);
    }
  };

  /**
   * 다시 활성화.
   *
   * ⚠️ 비활성화는 **논리 삭제**라 되돌릴 수 있는데(short_url.is_active 만 바꾼다),
   *    화면에는 되돌리는 길이 없었다 — 한 번 끄면 목록에서 영영 꺼진 채로 남았다.
   *    서버는 처음부터 PATCH { isActive: true } 를 지원하고 있었다(UpdateLinkRequest).
   * ⚠️ clear* 세 플래그는 계약상 필수라 항상 보낸다. 빠뜨리면 서버 기본값(false)이
   *    쓰여 결과는 같지만, 계약을 지키는 편이 나중에 기본값이 바뀌어도 안전하다.
   */
  const reactivate = async (link: Link) => {
    if (!window.confirm(`이 링크를 다시 활성화할까요?\n${link.shortUrl}\n\n이후 접속이 다시 연결됩니다.`)) return;
    setBusy(link.shortKey);
    setError(null);
    try {
      await linkApi.update(link.shortKey, {
        isActive: true,
        clearExpireAt: false,
        clearMaxClicks: false,
        clearPassword: false,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "처리에 실패했습니다.");
    } finally {
      setBusy(null);
    }
  };

  // 남이 만든 링크가 하나라도 있을 때만 필터를 보여준다 — 개인 사용자에게는 의미 없는 UI다
  const hasOthers = myAccountId !== undefined && links.some((l) => l.createdBy !== myAccountId);
  const visible = mineOnly && myAccountId !== undefined ? links.filter((l) => l.createdBy === myAccountId) : links;

  if (links.length === 0) {
    return <p className="subtitle">아직 만든 링크가 없습니다. 위에서 첫 링크를 만들어 보세요.</p>;
  }

  return (
    <>
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      {hasOthers && (
        <label className="checkbox">
          <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
          내가 만든 링크만 보기
        </label>
      )}

      <div className="table-scroll">
        <table className="link-table">
          <caption className="sr-only">내가 만든 단축 링크 목록</caption>
          <thead>
            <tr>
              <th scope="col">단축 링크</th>
              <th scope="col">원본</th>
              <th scope="col">클릭</th>
              <th scope="col">상태</th>
              <th scope="col">작업</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((link) => (
              <tr key={link.shortKey} className={link.isActive ? "" : "row-inactive"}>
                <td>
                  <code>{link.shortUrl}</code>
                  {link.hasPassword && <span className="badge" title="비밀번호 보호">🔒</span>}
                </td>
                <td className="truncate" title={link.originalUrl}>{link.originalUrl}</td>
                <td>
                  {link.clickCount}
                  {link.maxClicks !== null && <span className="hint"> / {link.maxClicks}</span>}
                </td>
                <td>
                  {/* 비활성은 배지 색도 바뀐다. 글자만 다르면 훑을 때 눈에 걸리지
                      않는다 — 색과 글자 둘 다로 말한다(색만으로는 말하지 않는다). */}
                  <span className={link.isActive ? "badge" : "badge badge-warn"}>
                    {link.isActive ? "활성" : "비활성"}
                  </span>
                  {link.expireAt && (
                    <div className="hint">~{new Date(link.expireAt).toLocaleDateString("ko-KR")}</div>
                  )}
                </td>
                <td>
                  <div className="row-actions">
                    <button type="button" className="btn-secondary" onClick={() => void copy(link)}>
                      {copied === link.shortKey ? "복사됨" : "복사"}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => onEdit(link)}
                      aria-label={`${link.shortUrl} 수정`}
                    >
                      수정
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => onShowQr(link)}>
                      QR
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => onShowStats(link)}>
                      통계
                    </button>
                    {link.isActive ? (
                      <button
                        type="button"
                        className="btn-outline-danger"
                        disabled={busy === link.shortKey}
                        onClick={() => void deactivate(link)}
                      >
                        비활성화
                      </button>
                    ) : (
                      /* 되살리는 동작이라 .btn-success 를 쓴다 — 바로 옆 자리에서
                         비활성화(윤곽선 위험)와 짝을 이룬다. 둘 다 중립 회색이면
                         '지금 어느 상태인지'가 버튼 글자를 읽어야만 보인다. */
                      <button
                        type="button"
                        className="btn-success"
                        disabled={busy === link.shortKey}
                        onClick={() => void reactivate(link)}
                      >
                        활성화
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
